import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { fadeIn, fadeOut, slideDown, slideUp } from 'src/app/animations';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { AssigneeService } from 'src/app/services/sections/job/assignee.service';
import { ActivatedRoute } from '@angular/router';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ToastType } from '../../toast/models/toast-type.enum';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { MatchContactFilter } from 'src/app/models/match-contact-filter.model';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ReplaceContact } from 'src/app/models/replace-contact.model';
import { Toast } from '../../toast/models/toast.model';
import { Assignee } from 'src/app/models/assignees/assignee.model';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';

import { sortBy } from 'lodash-es';

@Component({
	templateUrl: './assignees.component.html',
	styleUrls: ['./assignees.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss', '../../stylesheets/job-contacts.scss'],
	animations: [fadeOut(300), fadeIn(300), slideUp(100), slideDown(100)]
})
export class AssigneesComponent implements OnInit, OnDestroy, AfterViewInit {

	public assignees: Assignee[] = [];
	public selectedAssignee: Assignee = null;
	public customerPreferences: SettingsPreferences;
	public self = this;

	public readonly ButtonIcons = ButtonIcons;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;

	public isAssigneeActionInProgress = false;
	public isAssigneesListLoading = false;
	public mode: 'add' | 'edit' = 'add';
	public isFormOpen = false;

	public formModel: FormGroup;
	public isSaveBtnLocked = false;

	public formDataChange$: Subject<string> = new Subject<string>();
	private unsubscribe$: Subject<boolean> = new Subject<boolean>();

	public matchAssigneeFilter: MatchContactFilter = null;

	constructor(
		private activatedRoute: ActivatedRoute,
		public assigneeService: AssigneeService) { }


	ngOnInit(): void {
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;

		this.populateAssignees();

		this.formModel = new FormGroup({
			id: new FormControl(null),
			email: new FormControl(null, [Validators.email, Validators.minLength(3), Validators.pattern(/\S+/)]),
			firstName: new FormControl(null, [Validators.required, Validators.minLength(2), Validators.pattern(/\S+/)]),
			lastName: new FormControl(null, [Validators.required, Validators.minLength(2), Validators.pattern(/\S+/)]),
			phone: new FormControl(null, [Validators.pattern(/\S+/)]),
			title: new FormControl(null, [Validators.pattern(/\S+/)])
		});

		this.formDataChange$.pipe(
			takeUntil(this.unsubscribe$),
			tap(ev => this.isSaveBtnLocked = true),
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.matchAssigneeFilter = this.getMatchAssigeeFilter();
		});
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	public toggleSaveButtonLockState(userAlreadyExistsByEmailOrPhone): void {
		this.isSaveBtnLocked = userAlreadyExistsByEmailOrPhone;
	}

	public canSave(): boolean {
		const email = this.formModel.get('email').value;
		const phone = this.formModel.get('phone').value;
		return this.formModel.valid
			&& (email || phone)
			&& !this.isSaveBtnLocked;
	}

	public showMatchAssignee(): boolean {
		if (this.formModel.pristine) { return false; }

		const formData = this.formModel.getRawValue() as Assignee;
		return formData.firstName != null
			|| formData.lastName != null
			|| (formData.email != null && formData.email.length >= 5)
			|| (formData.phone != null && formData.phone.length >= 5);
	}

	public getMatchAssigeeFilter(): MatchContactFilter {
		const formData = this.formModel.getRawValue() as Assignee;
		if (!this.showMatchAssignee()) { return null; }
		return {
			firstName: formData.firstName,
			lastName: formData.lastName,
			emailAddress: formData.email,
			phoneNumber: formData.phone,
			assigneeId: formData.id
		} as MatchContactFilter;
	}

	private populateAssignees(): void {
		this.isAssigneesListLoading = true;

		this.assigneeService.getContacts(TokenHelper.getToken(this.activatedRoute))
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(assignees => {
				this.assignees = assignees;
				this.sortAssigneeList();
				this.isAssigneesListLoading = false;
			}, () => {
				this.isAssigneesListLoading = false;
			});
	}

	public editAssignee(model: Assignee): void {
		this.matchAssigneeFilter = null;
		if (!!model) {
			this.formModel.patchValue(model);
			this.mode = 'edit';
			this.selectedAssignee = model;
		} else {
			this.formModel.reset();
			this.mode = 'add';
		}

		this.toggleVisibleForm(true);
	}

	public deleteAssignee(): void {
		this.isAssigneeActionInProgress = true;
		this.assigneeService.deleteContact(TokenHelper.getToken(this.activatedRoute), this.selectedAssignee.id)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(deleted => {
				this.isAssigneeActionInProgress = false;
				if (deleted) {
					this.assignees = this.assignees.filter(item => item.id !== this.selectedAssignee.id);
					this.cancelEditAssignee();
				}
				else {
					ToastService.pushToast({
						icon: 'error',
						text: 'Unable to delete the assignee!',
						type: ToastType.Error
					} as Toast);
				}
			}, () => {
				this.isAssigneeActionInProgress = false;
				ToastService.pushToast({
					icon: 'error',
					text: 'Unable to delete the assignee!',
					type: ToastType.Error
				} as Toast);
			});
	}

	public cancelEditAssignee(): void {
		this.selectedAssignee = null;
		this.formModel.reset();
		this.isSaveBtnLocked = false;
		this.matchAssigneeFilter = null;
		this.toggleVisibleForm(false);
	}

	public saveAssignee(): void {
		this.isAssigneeActionInProgress = true;
		const model = this.formModel.getRawValue();

		if (!!model.id) {

			this.assigneeService.updateContact(TokenHelper.getToken(this.activatedRoute), model)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe(updated => {
					this.isAssigneeActionInProgress = false;

					if (updated) {
						const index = this.assignees.findIndex(x => x.id === model.id);
						this.assignees.splice(index, 1, model);
						this.sortAssigneeList();
						this.cancelEditAssignee();
					}
					else {
						ToastService.pushToast({
							icon: 'error',
							text: 'Unable to save the assignee!',
							type: ToastType.Error
						} as Toast);
					}
				}, () => {
					this.isAssigneeActionInProgress = false;
					ToastService.pushToast({
						icon: 'error',
						text: 'Unable to save the assignee!',
						type: ToastType.Error
					} as Toast);
				});
		} else {
			this.assigneeService.createContact(TokenHelper.getToken(this.activatedRoute), model)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe(assigneeId => {
					model.id = assigneeId;
					this.addAssigneeToList(model);
					this.cancelEditAssignee();
					this.isAssigneeActionInProgress = false;
				}, () => {
					this.isAssigneeActionInProgress = false;
					ToastService.pushToast({
						icon: 'error',
						text: 'Unable to save the assignee!',
						type: ToastType.Error
					} as Toast);
				});
		}
	}
	private addAssigneeToList(assignee: Assignee): void {
		this.assignees.push(assignee);
		this.sortAssigneeList();
	}

	private sortAssigneeList(): void {
		this.assignees = sortBy(this.assignees, [
			(item: Assignee) => item.lastName.toLowerCase(),
			(item: Assignee) => item.firstName.toLowerCase()
		]);
	}
	private toggleVisibleForm(isOpen = null): void {
		if (isOpen == null) {
			this.isFormOpen = !this.isFormOpen;
		} else {
			this.isFormOpen = isOpen;
		}
	}

	public replaceAssignee(replacement: ReplaceContact): void {
		this.isAssigneesListLoading = true;
		this.assigneeService.replaceContact(TokenHelper.getToken(this.activatedRoute), replacement.currentContactId, replacement.replacementRefId)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.isAssigneesListLoading = false;
				this.cancelEditAssignee();
				this.populateAssignees();
			}, (err) => {
				this.isAssigneesListLoading = false;
				ToastService.pushToast({
					icon: 'error',
					text: 'Unable to replace the assignee!',
					type: ToastType.Error
				} as Toast);

			});
	}

	public applyAssigneeToSite(assigneeRefId: number): void {
		this.isAssigneesListLoading = true;
		this.assigneeService.applyToSite(TokenHelper.getToken(this.activatedRoute), assigneeRefId)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(assignee => {
				this.isAssigneesListLoading = false;
				this.cancelEditAssignee();
				this.addAssigneeToList(assignee);
			}, (err) => {
				console.error(err);
				this.isAssigneesListLoading = false;
			});
	}

	public showAssignee(assigneeRefId: number): void {
		const assignee: Assignee = this.assignees.find(a => a.refId === assigneeRefId);
		if (assignee != null) {
			this.editAssignee(assignee);
		}
		else {
			console.warn('Assignee not found!');
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next(true);
		this.unsubscribe$.complete();
	}

}
